import React, { ReactNode, useCallback, useMemo } from 'react';
import { BaseFormItem } from '../base-form-item/BaseFormItem';
import { FieldRenderProps, Validator } from '../../form';
import { PasswordInput } from '../../input';

/**
 * {@docCategory PasswordInputFormItem}
 */
export interface PasswordInputFormItemProps {
   /**
    * The name of the input item.
    */
   name: string;

   /**
    * Whether or not the label should be rendered inline
    */
   inline?: boolean;

   /**
    * The label for the input item.
    */
   label?: string;

   /**
    * Set this to disable word wrap on the label text
    */
   disableLabelWordWrap?: boolean;

   /**
    * Allow the contents to stretch to its parent width
    */
   horizontalFill?: boolean;

   /**
    * The hint for the input item.
    */
   hint?: string;

   /**
    * The help text (info icon tooltip) for the input item.  Setting this value
    * will cause an info icon to be displayed that has a tooltip containing
    * this text.
    */
   helpText?: string;

   /**
    * The placeholder text for the input item.
    */
   placeholder?: string;

   /**
    * Set the input item to disabled.
    * @defaultvalue false
    */
   disabled?: boolean;

   /**
    * Enable the clear feature.
    * @defaultvalue false
    */
   allowClear?: boolean;

   /**
    * The prefix element.
    */
   prefix?: ReactNode;

   /**
    * The label text displayed before (on the left side of) the input field.
    */
   addonBefore?: ReactNode;

   /**
    * The label text displayed after (on the right side of) the input field.
    */
   addonAfter?: ReactNode;

   /**
    * Set the input item to be required.
    * @defaultvalue false
    */
   required?: boolean;

   /**
    * The maximum width that the label can occupy.  For inline labels, this is the total width
    * allocated to the label.
    */
   maxLabelWidth?: number | string;

   /**
    * The minimum number of characters that have to be entered for the value to be valid.
    */
   minLength?: number;

   /**
    * The maximum number of characters that can be entered for the value to be valid.
    */
   maxLength?: number;

   /**
    * Function to call to validate the data.
    */
   validator?: Validator;

   /**
    * Sets the width.
    */
   width?: string | number;

   /**
    * Sets the min width.
    */
   minWidth?: string | number;

   /**
    * Sets the max width.
    */
   maxWidth?: string | number;

   /**
    * Set this to focus on form item on first render
    */
   autoFocus?: boolean;

   /**
    * Callback called when the value is changed.
    * @param value - The value
    */
   onChange?: (value: string | undefined) => void;

   /**
    * Function called when the user presses the enter key.
    */
   onPressEnter?: React.KeyboardEventHandler<HTMLInputElement>;
}

export const PasswordInputFormItem = (props: PasswordInputFormItemProps) => {
   return (
      <BaseFormItem<string>
         name={props.name}
         inline={props.inline}
         label={props.label}
         disableLabelWordWrap={props.disableLabelWordWrap}
         horizontalFill={props.horizontalFill}
         hint={props.hint}
         helpText={props.helpText}
         required={props.required}
         maxLabelWidth={props.maxLabelWidth}
         minLength={props.minLength}
         maxLength={props.maxLength}
         validator={props.validator}
         render={fieldRenderer(props)}
      />
   );
};

const fieldRenderer = (props: PasswordInputFormItemProps) => {

   return (field: FieldRenderProps<string | undefined>) => {

      const onChange = props.onChange;
      const fieldInputOnChange = field.input.onChange;

      const onChangeHandler = useCallback((value?: string) => {
         fieldInputOnChange(value);
         if (onChange) {
            onChange(value);
         }
      }, [onChange, fieldInputOnChange]);

      const { name, placeholder, disabled, allowClear, prefix, addonBefore, addonAfter, width: widthProp, minWidth, maxWidth, autoFocus, onPressEnter, maxLength, horizontalFill } = props;
      const { fieldErrorClassName, input } = field;
      const { onBlur, onFocus, value } = input;

      const width = widthProp ? widthProp : (horizontalFill ? '100%' : undefined);

      return useMemo(() =>
         <PasswordInput
            name={name}
            className={fieldErrorClassName}
            placeholder={placeholder}
            disabled={disabled}
            allowClear={allowClear}
            prefix={prefix}
            addonBefore={addonBefore}
            addonAfter={addonAfter}
            maxLength={maxLength}
            value={value}
            width={width}
            minWidth={minWidth}
            maxWidth={maxWidth}
            autoFocus={autoFocus}
            onChange={onChangeHandler}
            onBlur={onBlur}
            onFocus={onFocus}
            onPressEnter={onPressEnter}
         />,[name, fieldErrorClassName, placeholder, disabled, allowClear, prefix, addonBefore, addonAfter, width, minWidth, maxWidth, autoFocus, onPressEnter, maxLength, onFocus, onBlur, value, onChangeHandler]
      );
   }
}
